import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, Upload, X, MessageCircle, Sparkles, Check, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import { api, formatApiError } from "../lib/api";
import { EVENTS, PERSONALITY, PREFERENCES, REVEAL_SCENES, tQuiz } from "../i18n/quiz";
import { analyzeQuiz } from "../i18n/quiz-analysis";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// ─────────────────────────────────────────────
// Helper: resize image client-side to JPEG dataURL (max 800px wide)
// ─────────────────────────────────────────────
async function fileToCompressedDataUrl(file, maxW = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.72));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────
// Stages
// ─────────────────────────────────────────────
const STAGES = {
  INTRO: "intro",
  EVENT: "event",
  QUESTION: "question",
  REVEAL: "reveal",
  FORM: "form",
  SUCCESS: "success",
};

export default function Quiz() {
  const navigate = useNavigate();
  const [stage, setStage] = useState(STAGES.INTRO);
  const [event, setEvent] = useState(null);
  const [qIndex, setQIndex] = useState(0); // 0-9
  const [answers, setAnswers] = useState({}); // { q1: 'val', ... q9: ['dataurl','dataurl'] }
  const [photos, setPhotos] = useState([]); // data URLs
  const [revealIndex, setRevealIndex] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", schedule_now: true, whatsapp_opt_in: true });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Compose 10-question list (5 personality + 5 preferences)
  const eventLabel = event ? event.label : "tu evento";
  const allQuestions = React.useMemo(
    () => [...PERSONALITY(eventLabel), ...PREFERENCES],
    [eventLabel]
  );
  const currentQ = allQuestions[qIndex];

  // Auto-advance reveal scenes
  useEffect(() => {
    if (stage !== STAGES.REVEAL) return;
    if (revealIndex >= REVEAL_SCENES.length - 1) return;
    const id = setTimeout(() => setRevealIndex((i) => i + 1), 4200);
    return () => clearTimeout(id);
  }, [stage, revealIndex]);

  // Reset reveal when entering reveal stage
  useEffect(() => {
    if (stage === STAGES.REVEAL) setRevealIndex(0); // eslint-disable-line
  }, [stage]);

  const start = () => setStage(STAGES.EVENT);

  const pickEvent = (ev) => {
    setEvent(ev);
    setQIndex(0);
    setStage(STAGES.QUESTION);
  };

  const answerAndNext = (val) => {
    setAnswers((a) => ({ ...a, [currentQ.id]: val }));
    nextStep();
  };

  const nextStep = () => {
    if (qIndex < allQuestions.length - 1) {
      setQIndex((i) => i + 1);
    } else {
      setStage(STAGES.REVEAL);
    }
  };

  const prevStep = () => {
    if (qIndex === 0) {
      setStage(STAGES.EVENT);
    } else {
      setQIndex((i) => i - 1);
    }
  };

  const handleFiles = async (files) => {
    const remaining = 3 - photos.length;
    const slice = Array.from(files).slice(0, remaining);
    try {
      const urls = await Promise.all(slice.map((f) => fileToCompressedDataUrl(f)));
      setPhotos((p) => [...p, ...urls]);
    } catch {
      toast.error("No se pudo procesar la imagen.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Completa tus datos.");
      return;
    }
    setSubmitting(true);
    // Pre-compute analysis so backend stores archetype/palette/trends
    const _analysis = analyzeQuiz(answers);
    const payload = {
      event_type: event?.key || "otro",
      answers,
      photos,
      archetype_key: _analysis.archetypeKey,
      palette_key: _analysis.paletteKey,
      trend_keys: (_analysis.trends || []).map((_t, i) =>
        (({
          editorial: ["editorial_eye", "high_shine", "bronze_smokey"],
          romantica: ["soft_glam", "glossy_lips", "monochrome"],
          clasica: ["retro_liner", "cherry_lip", "skin_first"],
          minimalista: ["skin_first", "monochrome", "freckled_skin"],
        })[_analysis.archetypeKey] || [])[i]
      ).filter(Boolean),
      ...form,
    };
    const tryPost = async () => api.post("/quiz", payload);
    try {
      let response;
      try {
        response = await tryPost();
      } catch (err) {
        // Retry once on transient errors (network / 5xx / 404 from stale routing)
        const status = err?.response?.status;
        if (!status || status === 404 || status >= 500) {
          await new Promise((r) => setTimeout(r, 800));
          response = await tryPost();
        } else {
          throw err;
        }
      }
      setResult(response.data);
      setStage(STAGES.SUCCESS);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isReveal = stage === STAGES.REVEAL;

  return (
    <>
      <Helmet>
        <title>Quiz personalizado · Encuentra tu look ideal · Abril Suarez</title>
        <meta name="description" content="Quiz gratuito de 10 preguntas para descubrir tu look ideal con Abril Suarez. Propuesta visual de cortesía y 20% de descuento si agendas tu entrevista inicial." />
        <link rel="canonical" href={`${typeof window !== "undefined" ? window.location.origin : ""}/quiz`} />
      </Helmet>

      {!isReveal && <Header onBook={() => { /* booking dialog not used inside quiz */ }} />}

      <main className={isReveal ? "" : "bg-[#FAFAFA] min-h-screen pt-28 pb-16"} data-testid="quiz-page">
        {stage === STAGES.INTRO && <IntroStage onStart={start} />}
        {stage === STAGES.EVENT && <EventStage onPick={pickEvent} />}
        {stage === STAGES.QUESTION && (
          <QuestionStage
            qIndex={qIndex}
            total={allQuestions.length}
            question={currentQ}
            answer={answers[currentQ.id]}
            onAnswer={answerAndNext}
            onNext={nextStep}
            onPrev={prevStep}
            photos={photos}
            setPhotos={setPhotos}
            handleFiles={handleFiles}
          />
        )}
        {isReveal && (
          <RevealStage
            index={revealIndex}
            onSkip={() => setStage(STAGES.FORM)}
            onContinue={() => setStage(STAGES.FORM)}
          />
        )}
        {stage === STAGES.FORM && (
          <FormStage
            form={form}
            setForm={setForm}
            onSubmit={submit}
            submitting={submitting}
          />
        )}
        {stage === STAGES.SUCCESS && result && (
          <ResultsStage
            result={result}
            event={event}
            answers={answers}
            personality={PERSONALITY(event?.label || "tu evento")}
            preferences={PREFERENCES}
            photos={photos}
            onHome={() => navigate("/")}
          />
        )}
      </main>
      {!isReveal && <Footer />}
    </>
  );
}

// ─────────────────────────────────────────────
// Sub-stages
// ─────────────────────────────────────────────
function IntroStage({ onStart }) {
  return (
    <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-12 gap-12 items-center" data-testid="quiz-intro">
      <div className="lg:col-span-7">
        <p className="overline text-[#8B6D5E] mb-4">{tQuiz.start_overline}</p>
        <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
          {tQuiz.start_title}
        </h1>
        <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">{tQuiz.start_sub}</p>
        <button className="btn-primary mt-10" onClick={onStart} data-testid="quiz-start-btn">
          <Sparkles size={14} /> {tQuiz.start_cta}
        </button>
      </div>
      <div className="lg:col-span-5">
        <div className="zoom-frame aspect-[3/4]">
          <img src="https://images.unsplash.com/photo-1503236823255-94609f598e71?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Inspiración" />
        </div>
      </div>
    </div>
  );
}

function EventStage({ onPick }) {
  return (
    <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12" data-testid="quiz-event-stage">
      <p className="overline text-[#8B6D5E] mb-4">Paso 0 · de 11</p>
      <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1]">{tQuiz.event_title}</h2>
      <p className="text-base text-[#5C5C5C] max-w-2xl mt-4">{tQuiz.event_sub}</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
        {EVENTS.map((ev) => (
          <button
            key={ev.key}
            onClick={() => onPick(ev)}
            data-testid={`event-${ev.key}`}
            className="text-left border border-[#1A1A1A]/15 hover:border-[#1A1A1A] hover:bg-[#F4F0EC]/40 p-6 transition-all group"
          >
            <p className="font-serif text-2xl tracking-tight group-hover:text-[#8B6D5E] transition-colors">{ev.label}</p>
            <p className="text-xs text-[#5C5C5C] mt-2">{ev.hint}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuestionStage({ qIndex, total, question, answer, onAnswer, onNext, onPrev, photos, setPhotos, handleFiles }) {
  const inputRef = useRef(null);
  const isPhotos = question.type === "photos";
  return (
    <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12" data-testid={`quiz-q-${qIndex + 1}`}>
      <div className="flex items-center justify-between mb-10">
        <p className="overline text-[#8B6D5E]">{tQuiz.progress} {qIndex + 1} · {total}</p>
        <div className="flex-1 mx-6 h-px bg-[#E8E1DA] relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#1A1A1A] transition-all duration-700"
            style={{ width: `${((qIndex + 1) / total) * 100}%` }}
          />
        </div>
        <p className="overline text-[#5C5C5C]">{Math.round(((qIndex + 1) / total) * 100)}%</p>
      </div>

      <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-3xl" data-testid="quiz-question-title">
        {question.title}
      </h2>

      <div className="mt-12">
        {!isPhotos && (
          <div className="space-y-3" data-testid="quiz-options">
            {question.options.map((opt) => {
              const selected = answer === opt.v;
              return (
                <button
                  key={opt.v}
                  onClick={() => onAnswer(opt.v)}
                  data-testid={`opt-${question.id}-${opt.v}`}
                  className={`w-full text-left border p-5 sm:p-6 flex items-center gap-5 transition-all ${
                    selected
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAFAFA]"
                      : "border-[#1A1A1A]/15 hover:border-[#1A1A1A] hover:bg-[#F4F0EC]/40"
                  }`}
                >
                  <span
                    className={`shrink-0 h-6 w-6 rounded-full border flex items-center justify-center ${
                      selected ? "border-[#FAFAFA] bg-[#8B6D5E]" : "border-[#1A1A1A]/40"
                    }`}
                  >
                    {selected && <Check size={14} className="text-[#FAFAFA]" />}
                  </span>
                  <span className="font-serif text-xl sm:text-2xl tracking-tight">{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {isPhotos && (
          <div data-testid="quiz-photos">
            <label className="overline text-[#5C5C5C] block mb-3">{tQuiz.upload_label}</label>
            <div
              className="border border-dashed border-[#1A1A1A]/30 p-10 text-center cursor-pointer hover:border-[#1A1A1A] transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFiles(e.dataTransfer.files);
              }}
              data-testid="photo-drop"
            >
              <Upload size={28} className="mx-auto text-[#8B6D5E] mb-3" />
              <p className="font-serif text-2xl">Arrastra o haz clic</p>
              <p className="text-xs text-[#5C5C5C] mt-2">{tQuiz.upload_hint}</p>
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="photo-input"
              />
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-4 mt-6">
                {photos.map((src, i) => (
                  <div key={i} className="relative group" data-testid={`photo-preview-${i}`}>
                    <div className="zoom-frame aspect-square">
                      <img src={src} alt={`Inspiración ${i + 1}`} />
                    </div>
                    <button
                      onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                      className="absolute top-2 right-2 bg-[#1A1A1A] text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`photo-remove-${i}`}
                      aria-label="Quitar"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
        <button onClick={onPrev} className="overline flex items-center gap-2 link-underline" data-testid="quiz-prev">
          <ArrowLeft size={14} /> {tQuiz.back}
        </button>
        {isPhotos ? (
          <button onClick={onNext} className="btn-primary" data-testid="quiz-photos-next">
            {photos.length === 0 ? tQuiz.skip : tQuiz.next} <ArrowRight size={14} />
          </button>
        ) : (
          answer && (
            <button onClick={onNext} className="btn-outline" data-testid="quiz-next">
              {qIndex === 9 ? tQuiz.see_results : tQuiz.next} <ArrowRight size={14} />
            </button>
          )
        )}
      </div>
    </div>
  );
}

function RevealStage({ index, onSkip, onContinue }) {
  const scene = REVEAL_SCENES[index];
  const isLast = index === REVEAL_SCENES.length - 1;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0E0E0E] text-[#FAFAFA] flex items-center justify-center overflow-hidden" data-testid="quiz-reveal">
      {REVEAL_SCENES.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img src={s.img} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/80" />
        </div>
      ))}

      <div className="relative z-10 max-w-3xl px-6 sm:px-10 text-center" key={`text-${index}`}>
        <p className="overline text-[#E8E1DA] mb-6 fade-in" data-testid="reveal-overline">{scene.overline}</p>
        <h2
          className={`font-serif tracking-tighter leading-[1.05] ${scene.accent ? "text-3xl sm:text-5xl lg:text-6xl italic" : "text-5xl sm:text-7xl lg:text-8xl"}`}
          style={{ animation: "rfade 1200ms ease both" }}
          data-testid="reveal-text"
        >
          {scene.text}
        </h2>

        {isLast && (
          <button onClick={onContinue} className="btn-primary mt-12 !bg-[#FAFAFA] !text-[#1A1A1A] !border-[#FAFAFA] hover:!bg-[#8B6D5E] hover:!text-[#FAFAFA] hover:!border-[#8B6D5E]" data-testid="reveal-continue">
            Ver mi propuesta <ArrowRight size={14} />
          </button>
        )}
      </div>

      {!isLast && (
        <button onClick={onSkip} className="absolute top-6 right-6 overline text-[#E8E1DA] hover:text-white z-20" data-testid="reveal-skip">
          Saltar →
        </button>
      )}

      {/* progress dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {REVEAL_SCENES.map((_, i) => (
          <span key={i} className={`h-[2px] w-8 transition-all ${i <= index ? "bg-[#FAFAFA]" : "bg-white/30"}`} />
        ))}
      </div>

      <style>{`@keyframes rfade { from { opacity:0; transform: translateY(18px);} to { opacity:1; transform: translateY(0);} }`}</style>
    </div>
  );
}

function FormStage({ form, setForm, onSubmit, submitting }) {
  return (
    <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12" data-testid="quiz-form-stage">
      <p className="overline text-[#8B6D5E] mb-4">{tQuiz.form_overline}</p>
      <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-3">{tQuiz.form_title}</h2>
      <p className="text-base text-[#5C5C5C] max-w-2xl mb-10">{tQuiz.form_sub}</p>

      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl" data-testid="quiz-form">
        <div className="sm:col-span-2">
          <label className="field-label">{tQuiz.form_name}</label>
          <input required className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quiz-name" />
        </div>
        <div>
          <label className="field-label">{tQuiz.form_email}</label>
          <input required type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quiz-email" />
        </div>
        <div>
          <label className="field-label">{tQuiz.form_phone}</label>
          <input required className="field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quiz-phone" />
        </div>

        <label className="sm:col-span-2 flex items-start gap-4 border border-[#1A1A1A]/20 p-5 cursor-pointer hover:border-[#1A1A1A] bg-[#F4F0EC]/50">
          <input
            type="checkbox"
            checked={form.schedule_now}
            onChange={(e) => setForm({ ...form, schedule_now: e.target.checked })}
            data-testid="quiz-schedule-now"
            className="mt-1 h-5 w-5 accent-[#1A1A1A]"
          />
          <span className="text-sm leading-relaxed text-[#1A1A1A]">{tQuiz.form_schedule}</span>
        </label>

        <label className="sm:col-span-2 flex items-start gap-4 border border-[#1A1A1A]/20 p-5 cursor-pointer hover:border-[#1A1A1A]">
          <input
            type="checkbox"
            checked={form.whatsapp_opt_in}
            onChange={(e) => setForm({ ...form, whatsapp_opt_in: e.target.checked })}
            data-testid="quiz-whatsapp-opt-in"
            className="mt-1 h-5 w-5 accent-[#1A1A1A]"
          />
          <span className="text-sm leading-relaxed text-[#1A1A1A]">
            Recibir mi propuesta y cupón por WhatsApp
            <span className="block text-xs text-[#5C5C5C] mt-1">
              Un mensaje único de Abril con tu código y el PDF. Sin spam.
            </span>
          </span>
        </label>

        <div className="sm:col-span-2">
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="quiz-submit">
            {submitting ? "Enviando…" : tQuiz.form_submit} <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}

function ShareActions({ leadId, discountCode }) {
  const [sending, setSending] = useState(false);
  const pdfUrl = `${BACKEND}/api/quiz/${leadId}/pdf`;
  const waMsg = encodeURIComponent(
    `Hola Abril! Acabo de terminar mi quiz. Aquí está mi propuesta personalizada en PDF: ${pdfUrl}\n\nMi código de cortesía: ${discountCode}`
  );
  const waHref = `https://wa.me/message/SG2ELRTG32LVF1?text=${waMsg}`;

  const sendEmail = async () => {
    try {
      setSending(true);
      await api.post(`/quiz/${leadId}/send-pdf`, {});
      toast.success("Enviado a tu correo. Revisa tu bandeja de entrada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="mb-20 border-y border-[#E8E1DA] py-8 grid sm:grid-cols-3 gap-3 sm:gap-2"
      data-testid="share-actions"
    >
      <a
        href={pdfUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between gap-4 border border-[#1A1A1A]/20 hover:border-[#1A1A1A] hover:bg-[#F4F0EC]/50 px-5 py-4 transition-all"
        data-testid="share-download-pdf"
      >
        <span>
          <span className="overline text-[#8B6D5E] block mb-1">Guarda tu propuesta</span>
          <span className="font-serif text-xl leading-tight">Descargar PDF</span>
        </span>
        <Download size={18} className="text-[#1A1A1A] group-hover:text-[#8B6D5E]" />
      </a>

      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between gap-4 border border-[#1A1A1A]/20 hover:border-[#1A1A1A] hover:bg-[#F4F0EC]/50 px-5 py-4 transition-all"
        data-testid="share-whatsapp"
      >
        <span>
          <span className="overline text-[#8B6D5E] block mb-1">Envíamela por WhatsApp</span>
          <span className="font-serif text-xl leading-tight">Compartir link</span>
        </span>
        <MessageCircle size={18} className="text-[#1A1A1A] group-hover:text-[#8B6D5E]" />
      </a>

      <button
        type="button"
        onClick={sendEmail}
        disabled={sending}
        className="group flex items-center justify-between gap-4 border border-[#1A1A1A]/20 hover:border-[#1A1A1A] hover:bg-[#F4F0EC]/50 px-5 py-4 transition-all disabled:opacity-60 text-left"
        data-testid="share-send-email"
      >
        <span>
          <span className="overline text-[#8B6D5E] block mb-1">Recíbela también por</span>
          <span className="font-serif text-xl leading-tight">
            {sending ? "Enviando…" : "Correo electrónico"}
          </span>
        </span>
        <Mail size={18} className="text-[#1A1A1A] group-hover:text-[#8B6D5E]" />
      </button>
    </div>
  );
}

function ResultsStage({ result, event, answers, personality, preferences, photos, onHome }) {
  // Helpers to find selected option label and image
  const findOpt = (questions, qid, val) => {
    const q = questions.find((x) => x.id === qid);
    if (!q || !q.options) return null;
    return q.options.find((o) => o.v === val) || null;
  };

  const pref = {
    q6: findOpt(preferences, "q6", answers.q6),
    q7: findOpt(preferences, "q7", answers.q7),
    q8: findOpt(preferences, "q8", answers.q8),
    q10: findOpt(preferences, "q10", answers.q10),
  };

  // Personality + preference analysis → archetype + recommendations
  const analysis = analyzeQuiz(answers);
  const { archetype, palette, trends } = analysis;

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 lg:py-20" data-testid="quiz-success">
      {/* HEADER */}
      <div className="grid lg:grid-cols-12 gap-10 mb-20">
        <div className="lg:col-span-7">
          <p className="overline text-[#8B6D5E] mb-4">{tQuiz.success_overline}</p>
          <h2 className="font-serif text-6xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95]" data-testid="results-title">
            {tQuiz.success_title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] max-w-xl">{tQuiz.success_sub}</p>
        </div>
        <div className="lg:col-span-5 lg:pl-8">
          <div className="bg-[#1A1A1A] text-[#FAFAFA] p-8">
            <p className="overline text-[#8B6D5E] mb-3">{tQuiz.res_code}</p>
            <p className="font-serif text-3xl sm:text-4xl tracking-widest mb-4" data-testid="discount-code">{result.discount_code}</p>
            <p className="text-sm text-[#A8A8A8] mb-5">
              <strong className="text-[#FAFAFA]">{result.service_discount}%</strong> en tu servicio · <strong className="text-[#FAFAFA]">{result.guests_discount}%</strong> para tus invitadas
            </p>
            <a
              href={`https://wa.me/message/SG2ELRTG32LVF1?text=${encodeURIComponent(`Hola Abril! Acabo de hacer el quiz, mi código es ${result.discount_code}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 overline text-[#FAFAFA] border-b border-[#8B6D5E] pb-1 hover:text-[#8B6D5E]"
              data-testid="success-whatsapp"
            >
              <MessageCircle size={14} /> {tQuiz.success_book}
            </a>
          </div>
        </div>
      </div>

      {/* SHARE / PDF ACTIONS */}
      <ShareActions leadId={result.id} discountCode={result.discount_code} />

      {/* ARCHETYPE */}
      <div className="mb-20 border-t border-[#E8E1DA] pt-12 grid lg:grid-cols-12 gap-10" data-testid="result-archetype">
        <div className="lg:col-span-5 lg:order-2">
          <div className="zoom-frame aspect-[4/5]">
            <img src={archetype.image} alt={archetype.name} />
          </div>
        </div>
        <div className="lg:col-span-7 lg:order-1">
          <p className="overline text-[#8B6D5E] mb-4">01 · Tu arquetipo</p>
          <p className="font-serif italic text-3xl text-[#8B6D5E] mb-3" data-testid="archetype-name">{archetype.name}</p>
          <h3 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6" data-testid="archetype-headline">
            {archetype.headline}
          </h3>
          <p className="text-base sm:text-lg text-[#5C5C5C] leading-relaxed max-w-2xl mb-8">
            {archetype.description}
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mt-8 border-t border-[#E8E1DA] pt-6">
            <div>
              <p className="overline text-[#8B6D5E] mb-3">Lo que te queda bien</p>
              <ul className="space-y-2">
                {archetype.affinity.map((a, i) => (
                  <li key={i} className="font-serif text-lg leading-snug">— {a}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="overline text-[#9E3C3C] mb-3">Lo que evitaríamos</p>
              <p className="font-serif text-lg leading-snug">— {archetype.avoid}</p>
            </div>
          </div>
        </div>
      </div>

      {/* RECOMMENDED PALETTE */}
      <div className="mb-20 border-t border-[#E8E1DA] pt-12" data-testid="result-recommended-palette">
        <div className="grid lg:grid-cols-12 gap-10 mb-8">
          <div className="lg:col-span-5">
            <p className="overline text-[#8B6D5E] mb-4">02 · Colores que se te verían increíble</p>
            <h3 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] italic text-[#1A1A1A]" data-testid="palette-name">
              Paleta {palette.name}
            </h3>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-base text-[#5C5C5C] leading-relaxed self-end">
            {palette.desc}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
          {palette.swatches.map((s, i) => (
            <div key={i} data-testid={`swatch-${i}`}>
              <div className="aspect-square w-full" style={{ background: s.hex }} />
              <p className="overline text-[#5C5C5C] mt-3">{s.hex}</p>
              <p className="font-serif text-lg tracking-tight mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* RECOMMENDED TRENDS */}
      <div className="mb-20 border-t border-[#E8E1DA] pt-12" data-testid="result-trends">
        <p className="overline text-[#8B6D5E] mb-4">03 · Tendencias para ti</p>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] mb-12">
          Tres looks que pediría tu estilo.
        </h3>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {trends.map((tr, i) => (
            <article key={i} className="border-t border-[#1A1A1A] pt-6" data-testid={`trend-${i}`}>
              <p className="font-serif italic text-5xl text-[#8B6D5E] leading-none mb-5">0{i + 1}</p>
              <h4 className="font-serif text-2xl tracking-tight mb-3">{tr.title}</h4>
              <p className="text-sm leading-relaxed text-[#5C5C5C]">{tr.desc}</p>
            </article>
          ))}
        </div>
      </div>

      {/* SELECTIONS RECAP — compact */}
      <div className="mb-16 border-t border-[#E8E1DA] pt-12" data-testid="result-recap">
        <p className="overline text-[#8B6D5E] mb-4">04 · Tus selecciones</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 mt-8">
          {event && (
            <div data-testid="recap-event">
              <p className="overline text-[#5C5C5C] mb-2">Evento</p>
              <p className="font-serif text-xl tracking-tight">{event.label}</p>
            </div>
          )}
          {[
            { key: "q6", label: tQuiz.res_palette, opt: pref.q6 },
            { key: "q7", label: tQuiz.res_flowers, opt: pref.q7 },
            { key: "q8", label: tQuiz.res_hair, opt: pref.q8 },
            { key: "q10", label: tQuiz.res_makeup, opt: pref.q10 },
          ].map((card) => (
            <div key={card.key} data-testid={`recap-${card.key}`}>
              <p className="overline text-[#5C5C5C] mb-2">{card.label}</p>
              <p className="font-serif text-xl tracking-tight leading-tight">{card.opt?.label?.split(" · ")[0] || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      {/* INSPIRATION PHOTOS */}
      {photos && photos.length > 0 && (
        <div className="mb-16 border-t border-[#E8E1DA] pt-10" data-testid="result-inspiration">
          <p className="overline text-[#8B6D5E] mb-3">05 · {tQuiz.res_inspiration}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-8 mt-8">
            {photos.map((src, i) => (
              <div key={i} className="zoom-frame aspect-[4/5]" data-testid={`result-photo-${i}`}>
                <img src={src} alt={`Inspiración ${i + 1}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FINAL CTA */}
      <div className="bg-[#F4F0EC] py-16 px-8 sm:px-16 grid lg:grid-cols-12 gap-8 items-center mt-16">
        <div className="lg:col-span-7">
          <p className="overline text-[#8B6D5E] mb-3">Próximo paso</p>
          <h3 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] mb-4">
            Llevemos este análisis al estudio.
          </h3>
          <p className="text-base text-[#5C5C5C] max-w-md">
            Agendemos tu entrevista inicial esta semana para activar tu descuento y diseñar el look completo a partir de este análisis.
          </p>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-3">
          <a
            href={`https://wa.me/message/SG2ELRTG32LVF1?text=${encodeURIComponent(`Hola Abril! Acabo de hacer el quiz, mi arquetipo es ${archetype.name} y mi código es ${result.discount_code}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary justify-center"
            data-testid="results-whatsapp"
          >
            <MessageCircle size={14} /> {tQuiz.success_book}
          </a>
          <button onClick={onHome} className="btn-outline justify-center" data-testid="success-home">
            {tQuiz.success_home}
          </button>
        </div>
      </div>
    </div>
  );
}
